import { useEffect, useState } from 'react';
import { CanvasTexture, type Texture } from 'three';

/**
 * Load land polygons (/data/land.geojson) and rasterize them into an
 * equirectangular land/ocean mask texture (white = land) the globe's land-fill
 * shader samples. Built in-browser on a canvas — no binary asset. Fetched once.
 */
interface LandGeoJSON {
  readonly features?: ReadonlyArray<{
    readonly geometry?: { readonly type?: string; readonly coordinates?: unknown };
  }>;
}

export function useLandMask(url: string, width = 2048): Texture | null {
  const [texture, setTexture] = useState<Texture | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((r) => (r.ok ? (r.json() as Promise<LandGeoJSON>) : Promise.reject(new Error(String(r.status)))))
      .then((geo) => {
        if (cancelled) return;
        const w = width;
        const h = Math.floor(width / 2);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#ffffff';

        const drawPolygon = (rings: number[][][]): void => {
          ctx.beginPath();
          for (const ring of rings) {
            for (let i = 0; i < ring.length; i += 1) {
              const pt = ring[i];
              const lon = pt?.[0];
              const lat = pt?.[1];
              if (lon === undefined || lat === undefined) continue;
              const x = ((lon + 180) / 360) * w;
              const y = ((90 - lat) / 180) * h;
              if (i === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            }
            ctx.closePath();
          }
          ctx.fill('evenodd');
        };

        for (const feature of geo.features ?? []) {
          const geom = feature.geometry;
          if (!geom?.coordinates) continue;
          if (geom.type === 'Polygon') {
            drawPolygon(geom.coordinates as number[][][]);
          } else if (geom.type === 'MultiPolygon') {
            for (const poly of geom.coordinates as number[][][][]) drawPolygon(poly);
          }
        }

        const tex = new CanvasTexture(canvas);
        tex.flipY = false; // canvas top row = lat +90 (north); matches the shader's v
        tex.needsUpdate = true;
        setTexture(tex);
      })
      .catch(() => {
        if (!cancelled) setTexture(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url, width]);

  return texture;
}
